import { MIME_CSV, MIME_XLSX } from './mimeTypes';


type PickResult = {
    isSupported: boolean;
    isCanceled?: boolean;
    isSuccess?: boolean;
    handle?: FileSystemFileHandle;
};

type SupportedType = 'csv' | 'xlsx';

function getFileTypeInfo(type: SupportedType) {
    switch (type) {
        case 'csv':
            return { mime: MIME_CSV, ext: '.csv', description: 'CSV File' };
        case 'xlsx':
            return { mime: MIME_XLSX, ext: '.xlsx', description: 'Excel File' };
    }
}


/** Does a file picker dialog, if it's supported */
export async function pickFileDestination(filename: string, type: SupportedType): Promise<PickResult> {
    if (!('showSaveFilePicker' in window)) {
        return { isSupported: false };
    }
    const { mime, ext, description } = getFileTypeInfo(type);
    try {
        const handle = await (window as any).showSaveFilePicker({
            suggestedName: filename.endsWith(ext) ? filename : filename + ext,
            types: [{ description, accept: { [mime]: [ext] } }],
            excludeAcceptAllOption: true,
        });
        return { isSupported: true, isCanceled: false, isSuccess: true, handle };
    } catch (error: any) {
        if (error.name === 'AbortError' || error.name === 'NotAllowedError') {
            return { isSupported: true, isCanceled: true, isSuccess: false };
        }
        throw error;
    }
}

export async function saveFileWithPicker(filename: string, blob: Blob, type: SupportedType): Promise<PickResult> {
    const pickResult = await pickFileDestination(filename, type);
    if (pickResult.isSuccess && pickResult.handle) {
        await saveBlobViaHandle(pickResult.handle!, blob);
    } else if (!pickResult.isSupported) {
        fallbackDownload(filename, blob, type);
    }
    return pickResult;
}

/** Saves file using handle if supplied. Otherwise saves file using the filename */
export async function saveBlob(
    filename: string,
    blob: Blob,
    type: SupportedType,
    handle?: FileSystemFileHandle,
) {
    if (handle) {
        await saveBlobViaHandle(handle, blob);
    } else {
        fallbackDownload(filename, blob, type);
    }
}

export async function saveBlobViaHandle(handle: FileSystemFileHandle, blob: Blob) {
    const writable = await handle.createWritable();
    await writable.write(blob);
    await writable.close();
}

export function fallbackDownload(filename: string, blob: Blob, type: SupportedType) {
    const { ext } = getFileTypeInfo(type);
    const fixedName = filename.endsWith(ext) ? filename : filename + ext;
    const url = URL.createObjectURL(blob);
    const link = document.createElement('a');
    link.href = url;
    link.download = fixedName;
    link.style.display = 'none';
    document.body.appendChild(link);
    link.click();
    document.body.removeChild(link);
    URL.revokeObjectURL(url);
}
